import { CalendarDate, DateValue } from '@internationalized/date';
import { Color, Spacing } from '@persona-id/design-tokens';
import { ArrowRightIcon } from '@persona-id/design-tokens/icons';
import { ZapIcon } from '@persona-id/design-tokens/icons/24px';
import { RangeValue } from '@react-types/shared';
import { isEmpty } from 'lodash/fp';
import { DateTime } from 'luxon';

import Pagination from 'dashboard/components/Pagination';
import { MonoSpan } from 'dashboard/components/Typography';
import DismissibleBanner from 'dashboard/components-lib/DismissibleBanner/DismissibleBanner';
import { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import TableControls, { FilterConfig } from 'dashboard/components-lib/TableControls';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import TableLayout, {
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { Event } from 'dashboard/models/event';
import { EventsFilterParams } from 'dashboard/redux/actions/events';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { LocalStorageKey } from 'lib/clients/StorageClient';
import { EventName } from 'lib/constants/events';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import { PaginationParams } from 'lib/interfaces/api';

const NAME_FILTER_OPTIONS = Object.values(EventName)
  .map((key) => ({ label: key, value: key }))
  .sort((a, b) => a.label.localeCompare(b.label));

interface DashboardEventsViewProps {
  events: Event[];
  isLoading: boolean;
  pageSize: number;
  paginationParams: PaginationParams;
  handlePageSizeClick: (pageSize: number) => void;
  handlePageLinkClick: (pageNumer: number) => void;
  filters: EventsFilterParams;
  handleDateUpdate: (range: RangeValue<DateValue> | null) => void;
  handleOmniSearch: (omniQuery: string) => void;
  handleNamesUpdate: (names: string[]) => void;
}

const DashboardEventsView = ({
  events,
  isLoading,
  pageSize,
  paginationParams,
  handlePageSizeClick,
  handlePageLinkClick,
  filters,
  handleDateUpdate,
  handleOmniSearch,
  handleNamesUpdate,
}: DashboardEventsViewProps) => {
  const { createdAtStart, createdAtEnd, omni, names } = filters;
  const empty = events.length === 0;

  const nameFilterLabelTextMapper = useLabelTextMapper(NAME_FILTER_OPTIONS, 'Event Types', {
    emptyIsAll: true,
  });

  const hasUsedFilters = !Object.values(filters).every(isEmpty);
  const currentTime = DateTime.local().minus({ months: 3 });

  const filterConfig: FilterConfig[] = [
    {
      id: 'name',
      name: 'Name',
      type: 'multiselect-legacy',
      selectOptionValues: NAME_FILTER_OPTIONS,
      labelTextMapper: nameFilterLabelTextMapper,
      value: names || [],
      onChange: handleNamesUpdate,
    },
    {
      id: 'created-at',
      name: 'Created at',
      type: 'daterange',
      value: { startDate: createdAtStart, endDate: createdAtEnd },
      onChange: handleDateUpdate,
      datePickerProps: {
        minDate: new CalendarDate(currentTime.year, currentTime.month, currentTime.day),
      },
    },
  ];

  return (
    <>
      <Container>
        <Header title='Events'></Header>
        <Section>
          <Card>
            <Card.Body>
              <Card.Title>Events</Card.Title>
              <DismissibleBanner localStorageKey={LocalStorageKey.EventsInfoBanner}>
                <Paragraph>
                  <span>
                    Events reflect the lifecycle of statuses that Persona resources move through and
                    contain a payload that can be viewed below. You can trigger webhooks or
                    workflows whenever an event occurs to take action on the resource&apos;s status
                    change.{' '}
                  </span>
                  <a
                    href='https://docs.withpersona.com/reference/types-of-events'
                    target='_blank'
                    rel='noreferrer'
                  >
                    Read more about events <ArrowRightIcon size='14' />
                  </a>
                </Paragraph>
              </DismissibleBanner>
              <TableControls
                searchProps={{
                  id: 'events-table__search',
                  handleInput: handleOmniSearch,
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  value: omni,
                  placeholder: 'Search by Name, Event ID, or Object ID',
                }}
                filters={filterConfig}
                css={{ marginTop: Spacing[2] }}
              />
              <TableLayout
                dataTest='events-view__events-table'
                isLoading={isLoading}
                isEmpty={empty}
                showHeader={hasUsedFilters}
                headerTR={
                  <>
                    <TableTH>Name</TableTH>
                    <TableTH>Event ID</TableTH>
                    <TableTH>Object ID</TableTH>
                    <TableTH>{`Created at (${DateTime.local().toFormat(
                      TIMEZONE_NAME_ABBREVIATED,
                    )})`}</TableTH>
                    <TableTH>Webhook triggers</TableTH>
                  </>
                }
                nullState={
                  <TableNullState dataTestBlock='events-view' hasUsedFilters={hasUsedFilters}>
                    <ZapIcon size={64} color={Color.BorderIconSecondary} />
                    <Spacer height={3} />
                    <div>View actions used to trigger Webhooks and Workflows.</div>
                    <Spacer height={5} />
                    <a
                      href='https://docs.withpersona.com/docs/events'
                      target='_blank'
                      rel='noreferrer'
                    >
                      Learn more about events
                    </a>
                  </TableNullState>
                }
              >
                {events.map((event) => (
                  <TableLinkTR
                    to={`/dashboard/events/${event.id}`}
                    key={event.id}
                    data-test={`event-table-row__${event.id}`}
                    id='event-table__row'
                  >
                    <TableTD>
                      <MonoSpan>{event.attributes.name}</MonoSpan>
                    </TableTD>
                    <TableTD>
                      <MonoSpan>{event.id}</MonoSpan>
                    </TableTD>
                    <TableTD>
                      {event.attributes.payload != null && (
                        <MonoSpan>{event.attributes.payload.data.id}</MonoSpan>
                      )}
                    </TableTD>
                    <TableTD>
                      {DateTime.fromISO(event.attributes['created-at']).toFormat(
                        DATE_AND_TIME_FORMAT,
                      )}
                    </TableTD>
                    <TableTD>{event.relationships['webhook-events'].data.length}</TableTD>
                  </TableLinkTR>
                ))}
              </TableLayout>
              <Pagination
                pageSize={pageSize}
                paginationParams={paginationParams}
                handlePageSizeClick={handlePageSizeClick}
                handlePageLinkClick={handlePageLinkClick}
              />
            </Card.Body>
          </Card>
        </Section>
      </Container>
    </>
  );
};

export default DashboardEventsView;
